import utilsMessages from 'utils/messages';
import { TableCell as TableCellStep, TableRow as TableRowStep } from './StepItem';
import Value, { Description } from './Value';

import { DeleteOutlined } from '@ant-design/icons';
import LazyImage from 'components/Image/LazyImage';
import { FrameImage } from 'components/Product/ProductImagesModal';
import ProductNote from 'components/ProductNote';
import QuantityInput from 'containers/QuantityInput';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CartItem, Product, ProductNoteInput, Supplier, TrackingAddToCartAddFromType } from 'types/schema';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import calculateVAT from 'utils/calculateVAT';
import generateProductSlug from 'utils/generateProductSlug';
import { getUomNameLocal } from '../../utils/getUomNameLocal';
import BButton from '../Button/Button';
import Money from '../Money/Money';
import noImage from './noIMG.svg';

const TableCell = styled(TableCellStep)`
  @media (min-width: 575px) {
    ${({ fixwidth }) =>
      fixwidth
        ? `
      width: 230px;
    `
        : ``};
  }
  @media (min-width: 769px) {
    ${({ fixwidth }) =>
      fixwidth
        ? `
      width: 320px;
    `
        : ``};
  }
  @media (max-width: 575px) {
    &:not(.hideOnMobile) {
      display: block;
    }
    &.quantity-row {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
    }
    &.padding-right-less {
      padding-right: 0;
    }
    padding: 5px 10px;
  }
  ${({ textAlign }) =>
    textAlign &&
    `
    text-align: ${textAlign};
  `}
  ${({ marginVertical }) =>
    marginVertical &&
    `
      display: block;
      margin: 4px 0px !important;
      padding: 12px 16px !important;
    `}
`;

const TableRow = styled(TableRowStep)`
  @media (max-width: 991px) {
    &:first-child {
      ${TableCell} {
        margin-top: 12px !important;
      }
    }
    &:last-child {
      ${TableCell} {
        margin-bottom: 0px !important;
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  background: #ffffff;
`;

const Button = styled(BButton)`
  height: auto;
  width: auto;
  color: #fe5043;
  font-size: 14px;
  &:visited,
  &:hover {
    path {
      fill: #fe5043;
    }
  }
`;

const StyledSpan = styled.span`
  display: flex;
  align-items: center;
  &.margin-right-10 {
    margin-right: 10px;
  }
`;

const NoWrapText = styled.span`
  white-space: nowrap;
`;

const FlexContainer = styled.div`
  width: 100%;
  min-height: 28px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

const Note = styled.p`
  color: #8c8c8c;
`;

const TableCellImageMb = styled.div<any>`
  display: flex;
`;

const ImageWrapper = styled.div<any>`
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border-radius: 4px;
  display: inline-block;
  height: 70px;
`;

const TableCellCtMb = styled.div<any>`
  flex: 1;
  padding-left: 8px;
`;

const PriceValueWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eaeaeb;
  margin-top: 8px;
  padding-top: 8px;
  padding-left: 78px;
`;

const PriceItem = styled.div`
  font-size: 14px;
  ${({ textAlign }) =>
    textAlign &&
    `
    text-align: ${textAlign};
  `}
`;

const PriceLabel = styled.div``;

const Quantity = styled.div``;

export type ProductsQuantityType = {
  [key: string]: number;
};
interface RowProps {
  name?: string;
  item: CartItem;
  supplier: Supplier;
  disabled?: boolean;
  onRemoveProduct: (item: CartItem) => void;
  productsQuantity: ProductsQuantityType;
  openNote: () => void;
  setProductNote?: (data: ProductNoteInput) => void;
}

interface ITableRowWithTracking {
  product: Product;
}
export const TableRowWithTracking: FC<ITableRowWithTracking> = (props) => {
  const { children } = props;

  return <TableRow>{children}</TableRow>;
};

export const TableRowContentMobile: FC<RowProps> = (props) => {
  const { item, disabled, onRemoveProduct, productsQuantity, openNote, name: stepName } = props;
  if (!item?.product) return null;

  const { id, imageUrl, note, uom, uomLocal, vat, name, supplierInternalCode, tagImageEn, tagImageVn, priceV2 } =
    item.product;
  const lang = localStorage.getItem('lang') || 'vi';
  const frameTagImage = lang === 'vi' ? tagImageVn : tagImageEn || '';

  const totalIncludedVat = useMemo(() => {
    const totalExcludedVAT =
      calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat * productsQuantity[id];
    const totalVATValue = Math.round((totalExcludedVAT * priceV2?.vat) / 100);
    return totalExcludedVAT + totalVATValue;
  }, [priceV2, productsQuantity[id]]);

  return (
    <>
      <TableCell marginVertical>
        <div>
          <TableCellImageMb>
            <ImageWrapper>
              <ItemImage noImage={!imageUrl} src={imageUrl || noImage} alt={name} />
              {frameTagImage && <FrameImage src={frameTagImage} />}
            </ImageWrapper>
            <TableCellCtMb>
              <Value>
                <a href={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`} target="_blank">
                  <Description>{name}</Description>
                </a>
              </Value>
              <PriceValueWrapper>
                <div>
                  <Money>{calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat}</Money>
                  <NoWrapText>/{getUomNameLocal(uomLocal)} </NoWrapText>
                </div>
                {disabled && <Quantity>x{productsQuantity[id]}</Quantity>}
              </PriceValueWrapper>
              {stepName?.includes('step-3') ? (
                note && <Note>{note}</Note>
              ) : (
                <ProductNote note={note} onClickOpenNote={openNote} />
              )}
            </TableCellCtMb>
            {!disabled && (
              <ButtonWrapper>
                <Button
                  type="secondary"
                  shape="circle"
                  icon={<DeleteOutlined />}
                  size="small"
                  style={{ color: 'rgba(0,0,0,0.54)' }}
                  ghost
                  onClick={onRemoveProduct(item)}
                />
              </ButtonWrapper>
            )}
          </TableCellImageMb>
          <div>
            {!disabled && ( // checkout step 1
              <FlexContainer>
                <QuantityInput
                  product={item.product}
                  size="small"
                  min={0.01}
                  allowDecimal={allowDecimalQuantity(uom)}
                  addFrom={TrackingAddToCartAddFromType.CartProduct}
                />
              </FlexContainer>
            )}
            <PriceInfo>
              <PriceItem>
                <PriceLabel>
                  <FormattedMessage {...utilsMessages.totalBeforeVatME} />
                </PriceLabel>
                <Money>
                  {calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat * productsQuantity[id]}
                </Money>
              </PriceItem>
              <PriceItem>
                <PriceLabel>VAT</PriceLabel>
                <div>{vat}%</div>
              </PriceItem>
              <PriceItem textAlign="right">
                <PriceLabel>
                  <FormattedMessage {...utilsMessages.totalAfterVatME} />
                </PriceLabel>
                <Money>{totalIncludedVat}</Money>
              </PriceItem>
            </PriceInfo>
          </div>
        </div>
      </TableCell>
    </>
  );
};

const ItemImage = styled(({ noImage, ...rest }) => <LazyImage {...rest} />)<any>`
  width: 70px;
  height: 70px;
  object-fit: ${({ noImage }) => (noImage ? 'contain' : 'cover')};
  ${({ noImage }) => (noImage ? `opacity: 0.3;` : '')};
`;

export const TableRowContentDesktop: FC<RowProps> = (props) => {
  const { item, disabled, onRemoveProduct, productsQuantity, openNote, name: stepName } = props;
  if (!item.product) return null;

  const { id, imageUrl, note, uom, uomLocal, vat, name, supplierInternalCode, tagImageEn, tagImageVn, priceV2 } =
    item.product;
  const lang = localStorage.getItem('lang') || 'vi';
  const frameTagImage = lang === 'vi' ? tagImageVn : tagImageEn || '';

  const totalIncludedVat = useMemo(() => {
    const totalExcludedVAT =
      calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat * productsQuantity[id];
    const totalVATValue = Math.round((totalExcludedVAT * priceV2?.vat) / 100);
    return totalExcludedVAT + totalVATValue;
  }, [priceV2, productsQuantity[id]]);

  return (
    <>
      <TableCell>
        <ImageWrapper>
          <ItemImage noImage={!imageUrl} src={imageUrl || noImage} alt={name} />
          {frameTagImage && <FrameImage src={frameTagImage} />}
        </ImageWrapper>
      </TableCell>
      <TableCell colSpan="2" fixwidth>
        <Link to={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`}>
          <Value>
            <Description>{name}</Description>
          </Value>
        </Link>
        {stepName?.includes('step-3') ? (
          note && <Note>{note}</Note>
        ) : (
          <ProductNote note={note} onClickOpenNote={openNote} />
        )}
      </TableCell>
      <TableCell className="hideOnMobile">
        <Money>{calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat || 0}</Money>
        <NoWrapText>/{getUomNameLocal(uomLocal)} </NoWrapText>
      </TableCell>
      {disabled && (
        <TableCell className="hideOnMobile">
          <span>
            {item.quantity} {getUomNameLocal(uomLocal)}
          </span>
        </TableCell>
      )}
      {!disabled && (
        <TableCell className="hideOnMobile">
          <QuantityInput
            product={item.product}
            size="small"
            min={0.01}
            allowDecimal={allowDecimalQuantity(uom)}
            addFrom={TrackingAddToCartAddFromType.CartProduct}
          />
        </TableCell>
      )}
      <TableCell className="hideOnMobile" textAlign="right">
        <Money>
          {calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat * productsQuantity[id]}
        </Money>
        {/* &nbsp;
        <NoWrapText>({vat}% VAT)</NoWrapText> */}
      </TableCell>
      <TableCell className="hideOnMobile" textAlign="right">
        <NoWrapText>{vat}%</NoWrapText>
      </TableCell>
      <TableCell className="hideOnMobile" textAlign="right">
        <Money>{totalIncludedVat}</Money>
        {/* &nbsp;
        <NoWrapText>({vat}% VAT)</NoWrapText> */}
      </TableCell>
      {!disabled && (
        <TableCell className="hideOnMobile">
          <Button
            type="secondary"
            shape="circle"
            icon={<DeleteOutlined />}
            size="small"
            style={{ color: 'rgba(0,0,0,0.54)' }}
            ghost
            onClick={onRemoveProduct(item)}
          />
        </TableCell>
      )}
    </>
  );
};
